import { forwardRef, useEffect, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from './core';

/** Full width unless the caller sets a base width: cn() joins classes, it doesn't resolve conflicts. */
const width = (className?: string) => (/(^|\s)w-\S+/.test(className ?? '') ? undefined : 'w-full');

const INPUT = 'rounded-xl border border-border bg-surface-2 px-3 text-text placeholder:text-muted/80 disabled:opacity-60 aria-[invalid=true]:border-danger';

export function Field({ label, hint, error, children, htmlFor, className, required }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: ReactNode; htmlFor: string; className?: string; required?: boolean }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-semibold">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
      {hint && !error && <p id={`${htmlFor}-hint`} className="text-xs text-muted">{hint}</p>}
      {error && (
        <p id={`${htmlFor}-error`} role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function TextInput({ className, invalid, ...rest }, ref) {
  return <input ref={ref} aria-invalid={invalid || undefined} className={cn(INPUT, width(className), 'min-h-11', className)} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cn(INPUT, width(className), 'min-h-20 py-2', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <select ref={ref} className={cn(INPUT, width(className), 'min-h-11 pr-8', className)} {...rest}>
      {children}
    </select>
  );
});

/** Parses a typed number. Empty string → null (not entered). Never turns "" into 0. */
export function parseNumberInput(raw: string): number | null | 'invalid' {
  const t = raw.trim().replace(',', '.');
  if (t === '') return null;
  if (!/^-?\d*\.?\d+$|^-?\d+\.$/.test(t)) return 'invalid';
  const n = Number(t);
  return Number.isFinite(n) ? n : 'invalid';
}

interface NumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value: number | null;
  onValueChange: (v: number | null) => void;
  decimals?: boolean;
  size?: 'md' | 'lg' | 'xl';
  invalid?: boolean;
  align?: 'left' | 'center' | 'right';
}

/**
 * Numeric input that keeps "empty" distinct from zero and uses the phone's number pad.
 * It keeps its own text while typing so "12." or "0.5" aren't mangled mid-entry.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput({ value, onValueChange, decimals = false, size = 'md', className, invalid, align = 'right', ...rest }, ref) {
  const [text, setText] = useState(value == null ? '' : String(value));
  const [bad, setBad] = useState(false);
  useEffect(() => {
    const parsed = parseNumberInput(text);
    if (parsed !== value && !(parsed === 'invalid')) setText(value == null ? '' : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      ref={ref}
      type="text"
      inputMode={decimals ? 'decimal' : 'numeric'}
      autoComplete="off"
      enterKeyHint="done"
      aria-invalid={invalid || bad || undefined}
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        const parsed = parseNumberInput(t);
        if (parsed === 'invalid') {
          setBad(true);
          return;
        }
        if (!decimals && parsed != null && !Number.isInteger(parsed)) {
          setBad(true);
          return;
        }
        setBad(false);
        onValueChange(parsed);
      }}
      className={cn(INPUT, width(className), 'tabular font-semibold', align === 'center' ? 'text-center' : align === 'left' ? 'text-left' : 'text-right', size === 'xl' ? 'min-h-16 text-3xl' : size === 'lg' ? 'min-h-13 text-2xl' : 'min-h-11 text-lg', className)}
      {...rest}
    />
  );
});

/** Big −/value/+ control for counting whole units. Empty (null) is shown as "—". */
export function Stepper({ id, label, value, onChange, min = 0, disabled }: { id: string; label: string; value: number | null; onChange: (v: number | null) => void; min?: number; disabled?: boolean }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-semibold">
        {label}
      </label>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          disabled={disabled || value == null || value <= min}
          onClick={() => onChange(value == null ? null : Math.max(min, value - 1))}
          aria-label={`Decrease ${label}`}
          className="flex size-16 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40"
        >
          <Minus className="size-7" aria-hidden />
        </button>
        <NumberInput id={id} value={value} onValueChange={onChange} size="xl" disabled={disabled} placeholder="—" align="center" className="min-w-0" />
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange((value ?? 0) + 1)}
          aria-label={`Increase ${label}`}
          className="flex size-16 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-2 disabled:opacity-40"
        >
          <Plus className="size-7" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <label htmlFor={id} className="font-semibold">
          {label}
        </label>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn('relative h-8 w-14 shrink-0 rounded-full border transition disabled:opacity-50', checked ? 'border-accent-fill bg-accent-fill' : 'border-border bg-surface-2')}
      >
        <span className={cn('absolute top-1 size-5.5 rounded-full bg-white shadow transition-all', checked ? 'left-7.5' : 'left-1')} />
      </button>
    </div>
  );
}

export function Checkbox({ checked, onChange, label, id }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; id?: string }) {
  const auto = useId();
  const cid = id ?? auto;
  return (
    <label htmlFor={cid} className="flex min-h-11 cursor-pointer items-center gap-3">
      <input id={cid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-5 accent-[var(--accent)]" />
      <span>{label}</span>
    </label>
  );
}

/** A big tap target that shows whether it is chosen (aria-pressed). */
export function ChoiceButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={selected} onClick={onClick} className={cn('min-h-14 rounded-2xl border-2 px-3 text-lg font-semibold', selected ? 'border-accent bg-accent-soft text-text' : 'border-border bg-surface hover:border-accent')}>
      {children}
    </button>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap rounded-xl border border-border bg-surface-2 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn('min-h-10 rounded-lg px-3 text-sm font-semibold', value === o.value ? 'bg-surface text-text shadow-sm' : 'text-muted hover:text-text')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
